import React, {useCallback, useEffect, useLayoutEffect, useRef, useState, useMemo} from "react";
import {relativeDate, relativeDuration} from "../../../util/timeConverter";
import {GetPlayersResponse, PlayerDetails, PlayerLite} from "../interfaces";
import {useLocalStorage} from "../../../hooks/useLocalStorage";
import TeamOverlay from "./TeamOverlay";
import PlayerNotesOverlay from "./PlayerNotesOverlay";
import PlayerStrikesOverlay from "./PlayerStrikesOverlay";
import {useOverlay} from "../overlay/OverlayContext";
import {BodyCell, BodyRow, BodyRowMapper, HeaderCell, HeaderRow, Table} from "../../../components/Table";
import Toggle from "../../../components/Toggle";
import {PlayerUpdateEvent, usePlayers} from "./PlayerContext";
import {useServerId} from "../useServerId";
import {doFetch} from "../../../util/fetch";
import {useEvent} from "../../../hooks/useEvent";

export default function Players(){

    const playerPageSize = 50;
    const serverId = useServerId()
    const players = usePlayers()
    const overlay = useOverlay()

    // Search state
    const [searchQuery, setSearchQuery] = useState<string>('')
    const [searchField, setSearchField] = useState<string>('displayName')
    const [isSearching, setIsSearching] = useState(false)
    const [searchResults, setSearchResults] = useState<PlayerDetails[]>([])
    const [showSearchResults, setShowSearchResults] = useState(false)

    // Quick filters
    const [onlineFilter, setOnlineFilter] = useState<'all' | 'online' | 'offline'>('all')
    const [currentWipe, setCurrentWipe] = useState(false)

    //#region Context Menu

    const [playerContextMenuData, setPlayerContextMenuData] = useState<{ posX: number, posY: number, player?: PlayerDetails }>({ posX: 0, posY: 0 })
    const contextRef = useRef<HTMLDivElement>(null!)

    const showContextMenu = (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>, player: PlayerDetails) => {
        e.preventDefault()
        setPlayerContextMenuData({...playerContextMenuData, posX: e.clientX, posY: e.clientY, player: player })
    }

    const closeContextMenu = () => {
        setPlayerContextMenuData({...playerContextMenuData, player: undefined })
    }

    useEffect(() => {
        const handleClick = () => {
            if (playerContextMenuData.player){
                setPlayerContextMenuData({...playerContextMenuData, player: undefined })
            }
        }

        window.addEventListener("click", handleClick)

        return () => {
            window.removeEventListener("click", handleClick)
        }
    }, [playerContextMenuData])

    useLayoutEffect(() => {
        if(playerContextMenuData.posX + contextRef.current?.offsetWidth > window.innerWidth){
            setPlayerContextMenuData({ ...playerContextMenuData, posX: playerContextMenuData.posX - contextRef.current?.offsetWidth})
        }
        if(playerContextMenuData.posY + contextRef.current?.offsetHeight > window.innerHeight){
            setPlayerContextMenuData({ ...playerContextMenuData, posY: playerContextMenuData.posY - contextRef.current?.offsetHeight})
        }
    }, [playerContextMenuData])

    //#endregion

    useEvent<PlayerUpdateEvent>("playerUpdate", e => {
        if (!e){
            return
        }

        // Remove players with the same steam id from offline players list
        const filteredOfflinePlayers = allOfflinePlayers.filter(p => p.steamId !== e.player.steamId)

        if (!e.player.isConnected) {
            // Add disconnected player to the start of offline players list
            setAllOfflinePlayers([e.player, ...filteredOfflinePlayers])
        } else {
            // Just remove the player from offline list when connected
            setAllOfflinePlayers(filteredOfflinePlayers)
        }

    })

    const [displayMode, setDisplayMode] = useLocalStorage("displayMode")

    const [strikesOverlayPlayer, setStrikesOverlayPlayer] = useState<PlayerLite>()
    const [notesOverlayPlayer, setNotesOverlayPlayer] = useState<PlayerLite>()
    const [teamOverlayId, setTeamOverlayId] = useState<number>(0)

    // Offline players pagination state
    const [pageInfo, setPageInfo] = useState<{ page: number, hasNextPage: boolean, totalOfflinePlayers: number }>({ page: 0, hasNextPage: false, totalOfflinePlayers: -1 })
    const [allOfflinePlayers, setAllOfflinePlayers] = useState<PlayerDetails[]>([])
    const [isLoadingOffline, setIsLoadingOffline] = useState(false)

    // Function to fetch a page of offline players. Page 0 replaces the list, later pages append.
    const fetchPage = useCallback(async (page: number) => {
        setIsLoadingOffline(true)
        const response = await doFetch<GetPlayersResponse>(`/api/client/players?serverId=${serverId}&pageSize=${playerPageSize}&page=${page}&currentWipe=${currentWipe}`)
        setIsLoadingOffline(false)
        if (response.data) {
            const data = response.data
            setAllOfflinePlayers(prev => page === 0 ? data.players : prev.concat(data.players))
            setPageInfo({ page: page, hasNextPage: data.hasNextPage, totalOfflinePlayers: data.playerCount })
        }
    }, [serverId, currentWipe])

    // (Re)load offline players whenever the server or filters change.
    // When only online players are requested, there is no need to load the offline list.
    useEffect(() => {
        if (onlineFilter === 'online') {
            setAllOfflinePlayers([])
            setPageInfo({ page: 0, hasNextPage: false, totalOfflinePlayers: 0 })
            return
        }
        fetchPage(0)
    }, [onlineFilter, fetchPage])

    // Load next page of offline players
    const loadNextPage = useCallback(() => {
        if (showSearchResults || onlineFilter === 'online') return

        if (pageInfo.hasNextPage && !isLoadingOffline) {
            const nextPage = pageInfo.page + 1
            fetchPage(nextPage)
        }
    }, [pageInfo, isLoadingOffline, fetchPage, showSearchResults, onlineFilter])

    // Handle search
    const handleSearch = useCallback(async (query: string) => {
        if (!query.trim()) {
            setShowSearchResults(false)
            return
        }

        setIsSearching(true)
        setShowSearchResults(true)

        try {
            const response = await doFetch<GetPlayersResponse>(
                `/api/client/players/search?serverId=${serverId}&q=${encodeURIComponent(query)}&field=${searchField}&limit=${playerPageSize}`
            )

            if (response.data) {
                setSearchResults(response.data.players)
            }
        } catch (error) {
            console.error('Search error:', error)
        } finally {
            setIsSearching(false)
        }
    }, [serverId, searchField, playerPageSize])

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            if (searchQuery) {
                handleSearch(searchQuery)
            } else {
                setShowSearchResults(false)
            }
        }, 300)

        return () => clearTimeout(timer)
    }, [searchQuery, handleSearch])

    // Combine online and offline players or show search results
    const allPlayers: PlayerDetails[] = useMemo(() => {
        if (showSearchResults) {
            return searchResults;
        }

        // Create a map to deduplicate by steamId, prioritizing online players
        const playerMap = new Map<string, PlayerDetails>();

        // Add offline players first (skipped when only online players are requested)
        if (onlineFilter !== 'online') {
            allOfflinePlayers.forEach(player => {
                playerMap.set(player.steamId, player);
            });
        }

        // Add online players (will overwrite offline versions of same players).
        // All online players are part of the current wipe, so they are unaffected by that filter.
        if (onlineFilter !== 'offline') {
            players.online.forEach(player => {
                playerMap.set(player.steamId, player);
            });
        }

        let result = Array.from(playerMap.values())
        result.sort((a, b) => b.lastSeen - a.lastSeen)
        result.sort((a, b) => (a.isConnected ? 0 : 1) - (b.isConnected ? 0 : 1))
        return result
    }, [showSearchResults, searchResults, players.online, allOfflinePlayers, onlineFilter]);

    return (
        <div className="size-full flex flex-col p-4 relative">

            {!!strikesOverlayPlayer && <PlayerStrikesOverlay player={strikesOverlayPlayer} hide={() => setStrikesOverlayPlayer(undefined)} />}
            {!!notesOverlayPlayer && <PlayerNotesOverlay player={notesOverlayPlayer} hide={() => setNotesOverlayPlayer(undefined)}/>}
            {teamOverlayId > 0 && <TeamOverlay teamId={teamOverlayId} hide={() => setTeamOverlayId(0)}/>}
            {playerContextMenuData.player && <PlayerContextMenu player={playerContextMenuData.player} />}

            <div className="flex justify-between items-center mb-4">
                <div>
                    <h1 className="text-2xl font-bold text-white">Player List</h1>
                    {showSearchResults ? (
                        <span><b>{searchResults.length}</b> search results for "{searchQuery}" in {searchField}</span>
                    ) : (
                        <>
                            <span><b>{players.online.length}</b> Online</span>
                            <span className="ml-4"><b>{pageInfo.totalOfflinePlayers}</b> Offline ({allOfflinePlayers.length} loaded)</span>
                        </>
                    )}
                    <div className="mt-2 flex flex-wrap items-center gap-4">
                        <Toggle label="Compact Mode" checked={displayMode === "compact"}
                                onChange={e => setDisplayMode(e.currentTarget.checked ? "compact" : "normal")}/>
                        <div className="flex flex-row gap-1 bg-neutral-700 p-1 rounded-md">
                            <FilterButton text="All" active={onlineFilter === 'all'} onClick={() => setOnlineFilter('all')}/>
                            <FilterButton text="Online" active={onlineFilter === 'online'} onClick={() => setOnlineFilter('online')}/>
                            <FilterButton text="Offline" active={onlineFilter === 'offline'} onClick={() => setOnlineFilter('offline')}/>
                        </div>
                        <div className="flex flex-row gap-1 bg-neutral-700 p-1 rounded-md">
                            <FilterButton text="All Time" active={!currentWipe} onClick={() => setCurrentWipe(false)}/>
                            <FilterButton text="Current Wipe" active={currentWipe} onClick={() => setCurrentWipe(true)}/>
                        </div>
                    </div>
                </div>
                <div className="self-end flex space-x-4 items-center">
                    <div className="flex border border-neutral-500 bg-neutral-800 rounded focus-within:outline-none focus-within:ring-2 focus-within:ring-primary focus-within:border-primary">
                        <select 
                            value={searchField}
                            onChange={(e) => setSearchField(e.target.value)}
                            className="relative border-0 bg-neutral-800 text-white rounded p-2 placeholder:text-neutral-500 focus:outline-none">
                            <option value="displayName">Display Name</option>
                            <option value="steamId">Steam ID</option>
                            <option value="ip">IP Address</option>
                        </select>
                        <div className="relative min-w-64">
                            <input 
                                type="text" 
                                placeholder="Search Players..."
                                className="w-full ps-3 border-l rounded-l-none border-0 border-neutral-500 bg-neutral-800 text-white rounded p-2 placeholder:text-neutral-500 focus:outline-none"
                                value={searchQuery} 
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && searchQuery.trim()) {
                                        handleSearch(searchQuery)
                                    }
                                }}
                            />
                            {searchQuery && (
                                <button 
                                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                                    onClick={() => {
                                        setSearchQuery('')
                                        setShowSearchResults(false)
                                    }}
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                    </div>
                    {isSearching && <span className="text-gray-400 ml-2">Searching...</span>}
                </div>
            </div>

            <Table onScrollBottom={loadNextPage}>
                <HeaderRow>
                    <HeaderCell></HeaderCell>
                    <HeaderCell sortKey="steamId">Steam ID</HeaderCell>
                    <HeaderCell sortKey="displayName">Display Name</HeaderCell>
                    <HeaderCell>IP</HeaderCell>
                    <HeaderCell sortKey="teamId">Team</HeaderCell>
                    <HeaderCell sortKey="totalPlayTime">Server Play Time</HeaderCell>
                    <HeaderCell sortKey="firstSeen">First Seen</HeaderCell>
                    <HeaderCell sortKey="lastSeen">Last Seen</HeaderCell>
                    <HeaderCell sortKey="strikeCount">Strikes</HeaderCell>
                    <HeaderCell sortKey="noteCount">Notes</HeaderCell>
                    <HeaderCell sortKey="steamVacBans">VAC Bans</HeaderCell>
                    <HeaderCell sortKey="steamGameBans">Game Bans</HeaderCell>
                    <HeaderCell sortKey="steamAccountCreated">Steam Account Created</HeaderCell>
                    <HeaderCell>Steam Aliases</HeaderCell>
                </HeaderRow>
                <BodyRowMapper hideEmpty={isSearching || isLoadingOffline} placeholder="No players on record" data={allPlayers} map={(player, compact) => (
                    <BodyRow key={player.steamId} highlight={!playerContextMenuData.player ? undefined : playerContextMenuData.player?.steamId === player.steamId}
                             onClick={() => !playerContextMenuData.player && overlay.openPlayerOverlay(player.steamId)}
                             onContextMenu={e => showContextMenu(e, player)}>
                        <BodyCell>
                            <span className={(player.isConnected ? "bg-green-500" : "bg-red-500") + " size-4 rounded-full block"}></span>
                        </BodyCell>
                        <BodyCell>{player.steamId}</BodyCell>
                        <BodyCell>{player.displayName}</BodyCell>
                        <BodyCell>{player.ip}</BodyCell>
                        <BodyCell onClick={player.teamId && player.teamId > 0 ? () => setTeamOverlayId(player.teamId ?? 0) : undefined}>
                            {player.teamId ?? "n/a"}
                        </BodyCell>
                        <BodyCell>{relativeDuration(player.totalPlayTime, true)}</BodyCell>
                        <BodyCell>{relativeDate(player.firstSeen, compact)}</BodyCell>
                        <BodyCell>{player.isConnected ? "now" : relativeDate(player.lastSeen, compact)}</BodyCell>
                        <BodyCell onClick={() => setStrikesOverlayPlayer(player)}>
                            {player.strikeCount > 0 ?
                                <span className="w-fit px-2 bg-red-500 rounded-md flex items-center justify-center">{player.strikeCount}</span> : "0"}
                        </BodyCell>
                        <BodyCell onClick={() => setNotesOverlayPlayer(player)}>
                            {player.noteCount}
                        </BodyCell>
                        <BodyCell>{(player.steamVacBans ?? 0) > 0 ? <span
                            className="w-fit px-2 bg-red-500 rounded-md flex items-center justify-center">{player.steamVacBans}</span> : "0"} {player.steamVacBans > 0 ? relativeDate(player.steamLastBanTime, true, true) : ""}</BodyCell>
                        <BodyCell>{(player.steamGameBans ?? 0) > 0 ? <span
                            className="w-fit px-2 bg-red-500 rounded-md flex items-center justify-center">{player.steamGameBans}</span> : "0"} {player.steamGameBans > 0 ? relativeDate(player.steamLastBanTime, true, true) : ""}</BodyCell>
                        <BodyCell>{player.steamAccountCreated > 0 ? relativeDate(player.steamAccountCreated, compact) : "-"}</BodyCell>
                        <BodyCell>{player.steamAliases.length > 0 ? player.steamAliases.join(', ') : "-"}</BodyCell>
                    </BodyRow>
                )}/>
                {(isLoadingOffline || isSearching) && (
                    <BodyRow>
                        <td colSpan={14}>
                            <div className="flex justify-center items-center py-4">
                                <span className="text-gray-400">
                                    {isSearching ? 'Searching players...' : 'Loading players...'}
                                </span>
                            </div>
                        </td>
                    </BodyRow>
                )}
            </Table>

        </div>
    )

    function FilterButton(props: {text: string, active: boolean, onClick: () => void}) {
        return <button onClick={props.onClick}
                       className={`rounded-md px-2 py-0.5 text-sm ${props.active ? "bg-primary" : "hover:bg-neutral-600"}`}>
            {props.text}
        </button>
    }

    function PlayerContextMenu({player}:{player: PlayerDetails}) {
        return <div ref={contextRef} style={{left: playerContextMenuData.posX, top: playerContextMenuData.posY}}
                    className="flex flex-col justify-center items-center fixed h-auto w-fit bg-gray-700 rounded-md min-w-10 overflow-hidden">
            <ul className="[&_*]:list-none [&_*]:w-full [&_*]:text-left">
                <li className="px-4 py-2 text-gray-500 cursor-default">{player.displayName} [{player.steamId}]</li>
                <li>
                    <button className="px-4 py-2 hover:bg-gray-600"
                            onClick={() => navigator.clipboard.writeText(player.steamId)}>
                        Copy Steam ID
                    </button>
                </li>
                <hr className="border-gray-500 my-1"/>
                <li>
                    <button className="px-4 py-2 hover:bg-gray-600"
                            onClick={() => overlay.openPlayerOverlay(player.steamId)}>
                        View Player Info
                    </button>
                </li>
                <li>
                    <button className="px-4 py-2 hover:bg-gray-600" onClick={() => setStrikesOverlayPlayer(player)}>
                        View Strikes
                    </button>
                </li>
                <li>
                    <button className="px-4 py-2 hover:bg-gray-600" onClick={() => setNotesOverlayPlayer(player)}>
                        View Notes
                    </button>
                </li>
                <hr className="border-gray-500 my-1"/>
                {player.isConnected && <li>
                    <button className="px-4 py-2 hover:bg-gray-600" onClick={() => overlay.openKickOverlay(player)}>
                        Kick Player
                    </button>
                </li>}
                {!player.isBanned ? <li>
                    <button className="px-4 py-2 hover:bg-gray-600" onClick={() => overlay.openBanOverlay(player)}>
                        Ban Player
                    </button>
                </li> : <li>
                    <button className="px-4 py-2 hover:bg-gray-600" onClick={() => overlay.openUnbanOverlay(player)}>
                        Unban Player
                    </button>
                </li>}
            </ul>
        </div>
    }
}